import { useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Plus, Pencil, Trash2, ArrowLeft, Upload, X, Check, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { getPosts, addPost, updatePost, deletePost, BlogPost } from '@/lib/store';
import { useToast } from '@/hooks/useToast';

function slugify(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Blog Post List ────────────────────────────────────────────────────────────
export function AdminBlogList() {
  const [posts, setPosts] = useState(() => getPosts());
  const { toast } = useToast();

  function handleDelete(id: string) {
    deletePost(id);
    setPosts(getPosts());
    toast({ title: 'Blog post deleted' });
  }

  function togglePublished(post: BlogPost) {
    updatePost(post.id, {
      published: !post.published,
      publishedAt: !post.published ? Date.now() : post.publishedAt,
    });
    setPosts(getPosts());
    toast({ title: post.published ? 'Post unpublished' : 'Post published' });
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Blog Posts</h1>
          <p className="text-stone-400 text-sm">{posts.length} total posts</p>
        </div>
        <Button asChild className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm">
          <Link to="/admin/blog/new"><Plus className="h-4 w-4 mr-1.5" /> New Post</Link>
        </Button>
      </div>

      <div className="space-y-3">
        {posts.sort((a, b) => b.createdAt - a.createdAt).map((post) => (
          <Card key={post.id} className="bg-[hsl(25,20%,12%)] border-white/10">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                {/* Thumbnail */}
                {post.imageUrl && (
                  <div className="hidden sm:block h-16 w-24 rounded-md overflow-hidden bg-stone-800 shrink-0">
                    <img src={post.imageUrl} alt={post.title} className="h-full w-full object-cover" />
                  </div>
                )}

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-stone-200 font-semibold text-sm leading-tight line-clamp-2">{post.title}</h3>
                    <Badge className={post.published
                      ? 'bg-green-900/40 text-green-400 border-0 shrink-0'
                      : 'bg-stone-800 text-stone-400 border-0 shrink-0'
                    }>
                      {post.published ? 'Live' : 'Draft'}
                    </Badge>
                  </div>
                  <p className="text-stone-500 text-xs mt-1 line-clamp-1">{post.excerpt}</p>
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-stone-500">
                    <span>{post.author}</span>
                    <span>{formatDate(post.createdAt)}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => togglePublished(post)}
                    className="text-stone-500 hover:text-stone-300 transition-colors p-1.5"
                    title={post.published ? 'Unpublish' : 'Publish'}
                  >
                    {post.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <Button asChild size="sm" variant="ghost" className="text-stone-400 hover:text-white h-8 w-8 p-0">
                    <Link to={`/admin/blog/${post.id}`}><Pencil className="h-3.5 w-3.5" /></Link>
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="ghost" className="text-stone-600 hover:text-red-400 h-8 w-8 p-0">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[hsl(25,20%,12%)] border-white/10">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Delete Post?</AlertDialogTitle>
                        <AlertDialogDescription className="text-stone-400">
                          This will permanently delete "{post.title}".
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-transparent border-white/10 text-stone-300">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(post.id)} className="bg-red-700 hover:bg-red-600 text-white">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {posts.length === 0 && (
          <Card className="border-dashed bg-transparent border-white/10">
            <CardContent className="py-16 text-center">
              <p className="text-stone-500">No blog posts yet.</p>
              <Button asChild className="mt-4 bg-amber-600 hover:bg-amber-500 text-white rounded-sm">
                <Link to="/admin/blog/new">Create Your First Post</Link>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ── Blog Post Form ────────────────────────────────────────────────────────────
export function AdminBlogForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const { toast } = useToast();

  const existing = !isNew ? getPosts().find((p) => p.id === id) : undefined;

  const [form, setForm] = useState<Omit<BlogPost, 'id' | 'createdAt'>>({
    title: existing?.title ?? '',
    slug: existing?.slug ?? '',
    excerpt: existing?.excerpt ?? '',
    content: existing?.content ?? '',
    imageUrl: existing?.imageUrl ?? '',
    author: existing?.author ?? 'Team DHO',
    published: existing?.published ?? false,
    publishedAt: existing?.publishedAt ?? Date.now(),
  });

  const [imagePreview, setImagePreview] = useState(existing?.imageUrl ?? '');
  const [saving, setSaving] = useState(false);
  const [autoSlug, setAutoSlug] = useState(isNew);

  function handleChange<K extends keyof typeof form>(field: K, value: typeof form[K]) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleTitleChange(title: string) {
    handleChange('title', title);
    if (autoSlug) handleChange('slug', slugify(title));
  }

  const handleImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      setImagePreview(dataUrl);
      handleChange('imageUrl', dataUrl);
    };
    reader.readAsDataURL(file);
  }, []);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...form,
        publishedAt: form.published && !existing?.publishedAt ? Date.now() : form.publishedAt,
      };
      if (isNew) {
        addPost(data);
        toast({ title: 'Post created!' });
      } else {
        updatePost(id, data);
        toast({ title: 'Post updated!' });
      }
      navigate('/admin/blog');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm" className="text-stone-400 hover:text-white">
          <Link to="/admin/blog"><ArrowLeft className="h-4 w-4 mr-1" /> Blog Posts</Link>
        </Button>
        <h1 className="text-2xl font-bold text-white">{isNew ? 'New Post' : 'Edit Post'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <Card className="bg-[hsl(25,20%,12%)] border-white/10">
          <CardHeader><CardTitle className="text-white text-base">Post Details</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-stone-300">Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="Post title…"
                required
                className="bg-[hsl(25,20%,8%)] border-white/10 text-white placeholder:text-stone-600"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-stone-300">
                URL Slug *
                <span className="text-stone-500 text-xs ml-2">shown in /blog/[slug]</span>
              </Label>
              <Input
                value={form.slug}
                onChange={(e) => { setAutoSlug(false); handleChange('slug', e.target.value); }}
                placeholder="post-url-slug"
                required
                className="bg-[hsl(25,20%,8%)] border-white/10 text-white font-mono placeholder:text-stone-600"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-stone-300">Excerpt</Label>
              <Textarea
                value={form.excerpt}
                onChange={(e) => handleChange('excerpt', e.target.value)}
                placeholder="Short description shown on the blog listing page…"
                rows={2}
                className="bg-[hsl(25,20%,8%)] border-white/10 text-white placeholder:text-stone-600 resize-none"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-stone-300">
                Content *
                <span className="text-stone-500 text-xs ml-2">Markdown supported</span>
              </Label>
              <Textarea
                value={form.content}
                onChange={(e) => handleChange('content', e.target.value)}
                placeholder="Write your post content here. Markdown formatting is supported: **bold**, # headings, etc."
                rows={12}
                required
                className="bg-[hsl(25,20%,8%)] border-white/10 text-white placeholder:text-stone-600 resize-y font-mono text-sm"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-stone-300">Author</Label>
              <Input
                value={form.author}
                onChange={(e) => handleChange('author', e.target.value)}
                placeholder="Team DHO"
                className="bg-[hsl(25,20%,8%)] border-white/10 text-white placeholder:text-stone-600"
              />
            </div>

            <div className="flex items-center justify-between bg-[hsl(25,20%,8%)] rounded-md px-4 py-3">
              <div>
                <Label className="text-stone-300 cursor-pointer">Published</Label>
                <p className="text-stone-600 text-xs">Visible to the public on the blog</p>
              </div>
              <Switch
                checked={form.published}
                onCheckedChange={(v) => handleChange('published', v)}
                className="data-[state=checked]:bg-green-600"
              />
            </div>
          </CardContent>
        </Card>

        {/* Image */}
        <Card className="bg-[hsl(25,20%,12%)] border-white/10">
          <CardHeader><CardTitle className="text-white text-base">Featured Image</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {imagePreview ? (
              <div className="relative w-full max-w-sm h-40">
                <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-md border border-white/10" />
                <button
                  type="button"
                  onClick={() => { setImagePreview(''); handleChange('imageUrl', ''); }}
                  className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full h-6 w-6 flex items-center justify-center hover:bg-red-500"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/15 rounded-md cursor-pointer hover:border-amber-600/50 transition-colors group">
                <Upload className="h-6 w-6 text-stone-600 group-hover:text-amber-600 mb-2" />
                <p className="text-stone-400 text-sm">Upload a featured image</p>
                <input type="file" accept="image/*" onChange={handleImageUpload} className="sr-only" />
              </label>
            )}
            <div className="space-y-1.5">
              <Label className="text-stone-300 text-xs">Or paste an image URL</Label>
              <Input
                value={form.imageUrl}
                onChange={(e) => { handleChange('imageUrl', e.target.value); setImagePreview(e.target.value); }}
                placeholder="https://…"
                className="bg-[hsl(25,20%,8%)] border-white/10 text-white placeholder:text-stone-600 text-sm"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button type="submit" disabled={saving} className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm px-6">
            <Check className="h-4 w-4 mr-1.5" />
            {saving ? 'Saving…' : isNew ? 'Publish Post' : 'Save Changes'}
          </Button>
          <Button asChild variant="outline" className="border-white/10 text-stone-300 hover:text-white rounded-sm">
            <Link to="/admin/blog">Cancel</Link>
          </Button>
        </div>
      </form>
    </div>
  );
}
